import { useEffect, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { auth, db } from '@/lib/firebase';
import { signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import { 
  ChevronLeft, 
  ChevronRight, 
  LayoutDashboard, 
  FolderTree, 
  UtensilsCrossed, 
  Info, 
  Images, 
  LogOut,
  User as UserIcon,
  Users as UsersIcon,
  History,
  Tags,
  Globe,
  Search
} from 'lucide-react';

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const [profile, setProfile] = useState<any>(null);
  const [isCollapsed, setIsCollapsed] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      const user = auth.currentUser;
      if (user) {
        const docRef = doc(db, 'users', user.uid);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setProfile(docSnap.data());
        }
      }
    };
    fetchProfile();
  }, []);

  const handleLogout = async () => {
    await signOut(auth);
    navigate('/admin/login');
  };

  const menuItems = [
    { 
      label: t('admin_nav_dashboard'), 
      path: '/admin', 
      icon: <LayoutDashboard size={20} />, 
      check: () => true // Everyone sees dashboard
    },
    { 
      label: t('admin_nav_activity'), 
      path: '/admin/activity', 
      icon: <History size={20} />, 
      check: (p: any) => p.role === 'administrator' 
    },
    { 
      label: t('admin_nav_users'), 
      path: '/admin/users', 
      icon: <UsersIcon size={20} />, 
      check: (p: any) => p.role === 'administrator' || p.permissions?.staff_management
    },
    { 
      label: t('admin_nav_categories'), 
      path: '/admin/categories', 
      icon: <FolderTree size={20} />, 
      check: (p: any) => p.role === 'administrator' || p.permissions?.menu_categories
    },
    { 
      label: t('admin_nav_menu'), 
      path: '/admin/menu', 
      icon: <UtensilsCrossed size={20} />, 
      check: (p: any) => p.role === 'administrator' || p.permissions?.menu_items
    },
    { 
      label: t('admin_nav_gallery_categories'), 
      path: '/admin/gallery-categories', 
      icon: <Tags size={20} />, 
      check: (p: any) => p.role === 'administrator' || p.permissions?.gallery_categories
    },
    { 
      label: t('admin_nav_gallery'), 
      path: '/admin/gallery', 
      icon: <Images size={20} />, 
      check: (p: any) => p.role === 'administrator' || p.permissions?.gallery_photos
    },
    { 
      label: t('admin_nav_landing_images') || 'Landing Images', 
      path: '/admin/landing-images', 
      icon: <Images size={20} />, 
      check: (p: any) => p.role === 'administrator'
    },
    { 
      label: t('admin_nav_business'), 
      path: '/admin/business', 
      icon: <Info size={20} />, 
      check: (p: any) => p.role === 'administrator'
    },
    { 
      label: 'SEO Settings', 
      path: '/admin/seo', 
      icon: <Search size={20} />, 
      check: (p: any) => p.role === 'administrator'
    },
  ];

  return (
    <div className="min-h-screen bg-black flex overflow-hidden">
      {/* Sidebar */}
      <aside 
        className={`bg-[#111] border-r border-white/5 p-4 flex flex-col transition-all duration-300 ease-in-out relative h-screen ${
          isCollapsed ? 'w-20' : 'w-72'
        }`}
      >
        {/* Toggle Button */}
        <button 
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="absolute -right-3 top-12 bg-[#F57F25] text-black rounded-full p-1 border-4 border-black hover:scale-110 transition-transform z-50"
        >
          {isCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>

        {/* Logo Section */}
        <div className={`mb-8 flex items-center shrink-0 ${isCollapsed ? 'justify-center' : 'px-2'}`}>
          <h2 className={`text-[#F57F25] font-black uppercase tracking-tighter transition-all ${
            isCollapsed ? 'text-xs' : 'text-2xl'
          }`}>
            {isCollapsed ? 'B' : 'Bucara Admin'}
          </h2>
        </div>

        {/* User Profile */}
        {profile && (
          <div className={`mb-6 p-3 bg-black/50 rounded-2xl border border-white/5 flex items-center gap-3 overflow-hidden shrink-0 ${
            isCollapsed ? 'justify-center' : ''
          }`}>
            <div className="w-8 h-8 bg-[#F57F25]/20 rounded-lg flex items-center justify-center text-[#F57F25] shrink-0">
              <UserIcon size={18} />
            </div>
            {!isCollapsed && (
              <div className="min-w-0">
                <p className="text-white font-bold text-xs truncate">{profile.firstName}</p>
                <p className="text-[#F57F25] text-[9px] font-black uppercase tracking-widest truncate">{profile.role}</p>
              </div>
            )}
          </div>
        )}

        {/* Navigation Section */}
        <nav className="flex-1 space-y-1.5 overflow-y-auto pr-2 custom-sidebar-scroll">
          {menuItems.map((item) => {
            if (profile && !item.check(profile)) return null;

            const isActive = location.pathname === item.path;

            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                title={isCollapsed ? item.label : ''}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-bold text-sm transition-all relative group ${
                  isActive
                    ? 'bg-[#F57F25] text-black shadow-lg shadow-[#F57F25]/20'
                    : 'text-white/40 hover:bg-white/5 hover:text-white'
                } ${isCollapsed ? 'justify-center' : ''}`}
              >
                <span className={`shrink-0 transition-transform group-hover:scale-110 ${isActive ? 'scale-110' : ''}`}>
                  {item.icon}
                </span>
                {!isCollapsed && <span className="truncate tracking-tight">{item.label}</span>}
                
                {isActive && !isCollapsed && (
                  <div className="absolute right-0 w-1 h-4 bg-black rounded-full"></div>
                )}
              </button>
            );
          })}
        </nav>

        {/* Language & Logout */}
        <div className="pt-4 border-t border-white/5 mt-4 shrink-0 space-y-2">
          {/* Language Switcher */}
          <button
            onClick={() => {
              const next = i18n.language === 'en' ? 'es' : 'en';
              i18n.changeLanguage(next);
            }}
            title={isCollapsed ? 'Change Language' : ''}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-bold text-sm text-white/40 hover:bg-white/5 hover:text-white transition-all ${
              isCollapsed ? 'justify-center' : ''
            }`}
          >
            <Globe size={20} className="shrink-0" />
            {!isCollapsed && (
              <span>{i18n.language === 'en' ? 'Español' : 'English'}</span>
            )}
          </button>

          {/* Logout Button */}
          <button
            onClick={handleLogout}
            title={isCollapsed ? t('admin_nav_logout') : ''}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-black text-xs uppercase tracking-widest text-red-500 hover:bg-red-500/10 transition-all ${
              isCollapsed ? 'justify-center' : ''
            }`}
          >
            <LogOut size={20} className="shrink-0" />
            {!isCollapsed && <span>{t('admin_nav_logout')}</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto p-6 md:p-10 custom-scrollbar bg-[#050505]">
        <div className="max-w-7xl mx-auto">
          <Outlet />
        </div>
      </main>

      <style dangerouslySetInnerHTML={{ __html: `
        .custom-sidebar-scroll::-webkit-scrollbar {
          width: 4px;
        }
        .custom-sidebar-scroll::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-sidebar-scroll::-webkit-scrollbar-thumb {
          background: rgba(245, 127, 37, 0.1);
          border-radius: 10px;
        }
        .custom-sidebar-scroll::-webkit-scrollbar-thumb:hover {
          background: rgba(245, 127, 37, 0.3);
        }
      `}} />
    </div>
  );
}
